'use client'

import { sendClientRequest } from './client-request'
import type { Session, User } from './types'
import type { PersonaSlug } from './personas'

/**
 * Steps 3-5 of sign-up.
 *
 * These sit apart from `lib/request.ts` because they authenticate differently.
 * Everything in that file talks to the `/auth/*` routes with the shared
 * `Auth-Key` — it has to, since those run before any session exists, and the key
 * is a server-only secret that never reaches a browser. From step 3 on there is
 * a session: the account was created the moment a contact point was verified,
 * and the token minted there is what proves the caller owns it. So these go out
 * from the browser with a bearer token, through `sendClientRequest`, and hit the
 * `authUser` routes on the API rather than the machine-to-machine ones.
 *
 * Each step returns the full user state, `nextStep` included, so the caller
 * never has to work out where the wizard goes next — the backend re-derives it
 * from the row it just wrote.
 */

export interface StepResult {
  status?: number
  error?: string
  code?: string
  data?: User
  /** `/user/password` returns the rule list alongside a rejection. */
  criteria?: { id: string, label: string, met: boolean }[]
}

/**
 * Step 3. Sets the first password on an account that has none.
 *
 * Not a password change: the backend refuses if one already exists, so this
 * cannot be used to overwrite a password from a stolen session. Accounts that
 * signed up through a provider skip this step — the provider is their credential.
 */
export const setPassword = async (password: string, session: Session): Promise<StepResult | false> => {
  if(!password) return false
  return await sendClientRequest({ url: '/user/password', method: 'POST', body: { password }, session })
}

export interface UsernameCheck {
  available: boolean
  username: string
  /** Error code naming why it was refused — `username.taken`, `field.username_charset`. Null when free. */
  reason: string | null
}

/**
 * Step 4a. Whether a handle can be claimed.
 *
 * Unauthenticated, and rate-limited server-side: the handle is picked on the
 * first screen in some entry paths, before a session exists. A refusal comes
 * back as a 200 with `available: false` and a reason code, not as an error —
 * "taken" is an answer this screen renders, not a failure.
 */
export const checkUsername = async (username: string, session?: Session): Promise<StepResult & { data?: any } | false> => {
  if(!username) return false
  return await sendClientRequest({ url: `/username/check?username=${encodeURIComponent(username)}`, method: 'GET', auth: false, session })
}

/** Step 4b. Claims the handle behind fuse.mn/{username}. */
export const claimUsername = async (username: string, session: Session): Promise<StepResult | false> => {
  if(!username) return false
  return await sendClientRequest({ url: '/user/username', method: 'POST', body: { username }, session })
}

export interface ProfileInput {
  name: string
  bio?: string
  avatar?: string | null
  /** Eight digits, no country code. Required to register as a QPay merchant. */
  phone?: string
  /** Who the page is for, from the first step. Also sets the account's entityType. */
  persona?: PersonaSlug
}

/** Step 5. Full name and phone, plus an optional bio and avatar. */
export const saveProfile = async ({ name, bio, avatar, phone, persona }: ProfileInput, session: Session): Promise<StepResult | false> => {
  if(!name) return false
  return await sendClientRequest({ url: '/user/profile', method: 'POST', body: { name, bio, avatar, phone, persona }, session })
}

/**
 * The persona on its own, for an account that is past the profile step: a
 * Google sign-up (Google supplied the name, so that step is skipped) or a
 * sign-up resumed from before personas existed.
 */
export const savePersona = async (persona: PersonaSlug, session: Session): Promise<StepResult | false> =>
  await sendClientRequest({ url: '/user/profile', method: 'POST', body: { persona }, session })

/**
 * Handles the account could claim, derived server-side from the name it saved
 * on the previous step.
 *
 * Takes no arguments on purpose: the backend reads the name off the
 * authenticated row, so there is no way for a caller to ask for suggestions
 * about someone else. An empty list is a normal answer — a name that
 * transliterates to nothing usable has nothing to offer — so the screen treats
 * it as "no panel", never as an error.
 */
export const suggestUsernames = async (session: Session): Promise<StepResult & { data?: string[] } | false> => {
  return await sendClientRequest({ url: '/username/suggest', method: 'GET', session })
}

/**
 * Re-reads where sign-up stopped.
 *
 * The session carries `nextStep` from whichever step last ran, but a session
 * outlives the tab it was made in — someone who abandoned the wizard and came
 * back days later resumes from the row, not from a stale token.
 */
export const fetchAuthState = async (session: Session): Promise<StepResult | false> => {
  return await sendClientRequest({ url: '/user', method: 'GET', session })
}
